import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Entity, TypeData } from '../../../model/entity.model';
import { OotbEntityService } from '../../../services/ootb-entity.service';
import { LoadingService } from '../../../loading.service';
import { DefaultValueEditorComponent } from '../default-value-editor/default-value-editor.component';
import { formatShapeSummary } from '../../../shared/geometry/shape-description';

@Component({
    selector: 'app-ootb-entity',
    imports: [CommonModule, FormsModule, DefaultValueEditorComponent],
    templateUrl: './ootb-entity.component.html'
})
export class OotbEntityComponent {

  booleanOptions = [
  { label: 'True', value: true },
  { label: 'False', value: false }
];


  @Input() entityData: Entity | TypeData = {
    name: '',
    type: 'TABLE',
    id: '',
    status: '',
    data: [
      {
        name: '',
        type: 'UUID',
        isUnique: false,
        primaryKey: false,
        startIncrementFrom: 0,
        referenceEntityName: '',
        referenceEntityColumnName: 'id',
        id: '',
        data: [],
        defaultValue: '',
      },
    ],
  };
  @Output() entityDataChange = new EventEmitter<string>();
  @Input() index: number = 0;
  @Input() modelNameInput: string = '';
  @Input() editFlag: boolean = false;
  @Output() exit = new EventEmitter<boolean>();

  modelName = '';
  status = '';
  ootbEntityList: Entity[] = [];
  referenceEntityOptions: Array<{ id: string; name: string }> = [];

  dataTypeUniqueFlag = true;

  dataTypes = [
    'STRING',
    'NUMBER',
    'BOOLEAN',
    'ARRAY_OF_OBJECT',
    'AUTO_INCREMENT',
    'ENTITY_REFERENCE',
    // 'SHARED_ENTITY_REFERENCE',
    'ATTACHMENT',
    'TIMESTAMP',
    'GEOMETRY'
  ];
  // if auto_increment we can have a variable which say from which number to start
  addVariableFlag = false;
  newVariableUniqueFlag = true;
  addVariableButtonFlag = true;
  newVariableData: TypeData = {
    name: '',
    type: 'STRING',
    isUnique: false,
    primaryKey: false,
    startIncrementFrom: 0,
    referenceEntityName: '',
    referenceEntityColumnName: 'id',
    multiple: false,
    extendedAttributes: false,
    id: '',
    data: [],
    defaultValue: '',
  };
  colors = ['border-red-700', 'border-green-500', 'border-indigo-500'];

  /** Glossary-worded Geometry summary (`Area · 4 points`, `Empty shape`, …) for the read-only Properties cell. */
  formatShapeSummary(value: any): string {
    return formatShapeSummary(value);
  }

  showExitWarning = false;

  constructor(
    public ootbEntityService: OotbEntityService, // Used for saving OOTB entities
    private loading: LoadingService,
    // keep constructor lean; no extra services
  ) {
    // Only add escape key listener to the top-level entity component
    if (this.index === 0) {
      document.addEventListener('keydown', this.handleEscapeKey.bind(this));
    }
  }

  handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      // Prevent the event from bubbling up to parent components
      event.stopPropagation();
      
      if (this.addVariableFlag) {
        this.addVariableChangeFlag();
      } else if (this.index === 0) { // Only show exit warning for the root component
        this.showExitWarning = true;
      }
    }
  }

  ngOnInit() {
    if ('status' in this.entityData) {
      this.status = this.entityData.status;
      if (this.status == 'NEW') {
        this.editFlag = true;
      }
    }
    if (!this.index) {
      this.modelName = this.entityData.name;
      this.ootbEntityService.$values.subscribe((value: Entity[] | undefined) => {
        this.ootbEntityList = value ?? [];
        this.rebuildReferenceOptions(this.ootbEntityList);
      });

      // Ensure OOTB entities are available even when user opens OOTB manager directly
      this.ootbEntityService.query(1000);
    } else {
      this.modelName = this.modelNameInput + '_' + this.entityData.name;
    }
    
  }

  private rebuildReferenceOptions(allEntities: Entity[]) {
    const currentEntityId = 'id' in this.entityData ? this.entityData.id : '';
    const currentEntityName = this.entityData.name;

    const options = (allEntities ?? [])
      .filter((e) => e?.type === 'TABLE' && !!e?.id)
      .map((e) => ({ id: e.id, name: e.name }))
      // If entity isn't saved yet (no id), fall back to excluding by name
      .filter((e) => (currentEntityId ? e.id !== currentEntityId : e.name !== currentEntityName));

    this.referenceEntityOptions = options;
  }

  getColorClass(index: number): string {
    return this.colors[index % this.colors.length];
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('modelNameInput' in changes) {
      this.tableNameChanged();
    }
  }
  /**
   * True when THIS editor instance represents an ADVANCED (extendedAttributes) AOB — such an AOB holds
   * dynamic user-defined custom fields at runtime, so its design-time child set is frozen (no adds).
   */
  get isAdvancedAobContainer(): boolean {
    return !!(this.entityData as TypeData).extendedAttributes;
  }

  addVariableChangeFlag() {
    this.newVariableData = {
      name: '',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      extendedAttributes: false,
      id: '',
      data: [],
      defaultValue: '',
    };
    this.addVariableFlag = !this.addVariableFlag;
  }

  newVariableTypeChanged() {
    this.addVariableButtonFlag = true;
    this.newVariableData.data = [];

    // Advanced AOB-only flag — and only on a TOP-LEVEL AOB (index === 0). A nested AOB is never advanced.
    if (this.newVariableData.type !== 'ARRAY_OF_OBJECT') {
      this.newVariableData.extendedAttributes = false;
    } else {
      this.newVariableData.extendedAttributes = this.index === 0 ? !!this.newVariableData.extendedAttributes : false;
    }

    if (this.newVariableData.type == 'STRING') {
      this.newVariableData.defaultValue = '';
      this.newVariableUniqueFlag = true;
    } else if (this.newVariableData.type == 'NUMBER') {
      this.newVariableData.defaultValue = 0;
    } else if (this.newVariableData.type == 'AUTO_INCREMENT') {
      this.newVariableData.startIncrementFrom = 0;
      this.newVariableData.defaultValue = 0;
      this.newVariableUniqueFlag = false;
    } else if (this.newVariableData.type == 'ARRAY_OF_STRING') {
      this.newVariableUniqueFlag = false;
    } else if (this.newVariableData.type == 'ARRAY_OF_NUMBER') {
      this.newVariableUniqueFlag = false;
    } else if (this.newVariableData.type == 'ARRAY_OF_OBJECT') {
      this.newVariableUniqueFlag = false;
    } else if (this.newVariableData.type == 'BOOLEAN') {
      this.newVariableUniqueFlag = false;
      this.newVariableData.defaultValue = false;
    } else if (this.newVariableData.type == 'TIMESTAMP') {
      this.newVariableUniqueFlag = false;
      this.newVariableData.defaultValue = new Date().toISOString();
    } else if (this.newVariableData.type == 'GEOMETRY') {
      this.newVariableUniqueFlag = false;
      this.newVariableData.defaultValue = { type: 'Polygon', coordinates: [] };
    } else if (this.newVariableData.type == 'UUID') {
      this.newVariableUniqueFlag = false;
      this.newVariableData.primaryKey = false;
      this.newVariableData.defaultValue = '';
    } else if (this.newVariableData.type == 'ATTACHMENT') {
      this.newVariableUniqueFlag = false;
      this.newVariableData.primaryKey = false;
      this.newVariableData.defaultValue = '';
    }else if (this.newVariableData.type == 'ENTITY_REFERENCE') {
      this.newVariableUniqueFlag = false;
      this.addVariableButtonFlag = false;
      this.newVariableData.referenceEntityName = '';
      this.newVariableData.referenceEntityColumnName = 'id';
    } 
    // else if (this.newVariableData.type == 'SHARED_ENTITY_REFERENCE') {
    //   this.newVariableUniqueFlag = false;
    //   this.addVariableButtonFlag = false;
    //   this.newVariableData.referenceEntityName = '';
    //   this.newVariableData.referenceEntityColumnName = '';
    //   this.newVariableData.defaultValue = null;
    // }
     else {
      this.newVariableUniqueFlag = true;
    }
  }
  newVariableReferenceEntityChanged() {
    if (this.newVariableData.referenceEntityName !== '') {
      this.addVariableButtonFlag = true;
    }
  }
  serTargetEntityChanged() {
    // For SER, only enable button when both entity and column name are provided
    if (this.newVariableData.referenceEntityName !== '' && 
        this.newVariableData.referenceEntityColumnName !== '') {
      this.addVariableButtonFlag = true;
    } else {
      this.addVariableButtonFlag = false;
    }
  }
  serReverseColumnNameChanged() {
    // Validate column name (alphanumeric and underscore only)
    this.newVariableData.referenceEntityColumnName = 
      this.newVariableData.referenceEntityColumnName.replace(/[^a-zA-Z0-9_]/g, '');
    
    // Enable button only if both fields are filled
    if (this.newVariableData.referenceEntityName !== '' && 
        this.newVariableData.referenceEntityColumnName !== '') {
      this.addVariableButtonFlag = true;
    } else {
      this.addVariableButtonFlag = false;
    }
  }
  getTargetEntityName(): string {
    if (!this.newVariableData.referenceEntityName) {
      return 'target entity';
    }
    const targetEntity = this.referenceEntityOptions.find(
      (e) => e.id === this.newVariableData.referenceEntityName,
    );
    return targetEntity ? targetEntity.name : 'target entity';
  }
  
  getEntityNameById(entityId: string): string {
    if (!entityId) {
      return entityId;
    }
    const entity = this.referenceEntityOptions.find((e) => e.id === entityId);
    return entity ? entity.name : entityId;
  }
  // validations
  newVaraibleStartIncementChange() {
    if (this.newVariableData.startIncrementFrom < 0) {
      this.newVariableData.startIncrementFrom = 0;
    }
  }
  addVariable() {
    this.newVariableData.name = this.newVariableData.name.replace(
      /[^a-zA-Z_]/g,
      ''
    );
    this.newVariableData.id =
      this.newVariableData.name + '_' + this.generateTableId();

    const isUnique = this.uniqueVariableNameCheck();
    if (!isUnique) {
      alert('same variable name already exists, please change');
      return;
    }

    if (this.newVariableData.type == 'ARRAY_OF_OBJECT') {
      // Advanced is allowed ONLY on a top-level AOB (index === 0); a nested AOB is never advanced.
      this.newVariableData.extendedAttributes = this.index === 0 ? !!this.newVariableData.extendedAttributes : false;
      this.newVariableData.data = [
        {
          name: 'id',
          type: 'UUID',
          isUnique: true,
          primaryKey: true,
          startIncrementFrom: 0,
          referenceEntityName: '',
          referenceEntityColumnName: 'id',
          id: '',
          data: [],
          defaultValue: '',
        },
        {
          name: 'id_auto_increment',
          type: 'AUTO_INCREMENT',
          isUnique: true,
          primaryKey: false,
          startIncrementFrom: 0,
          referenceEntityName: '',
          referenceEntityColumnName: 'id',
          id: '',
          data: [],
          defaultValue: 0,
        },
        {
          name: 'parent_id',
          type: 'ENTITY_REFERENCE',
          isUnique: true,
          primaryKey: false,
          startIncrementFrom: 0,
          referenceEntityName: 'id' in this.entityData ? this.entityData.id : '',
          referenceEntityColumnName: 'id',
          id: '',
          data: [],
          defaultValue: '',
        },
      ];

      if (this.newVariableData.extendedAttributes) {
        this.newVariableData.data.push(
          {
            name: 'systemKey',
            type: 'STRING',
            isUnique: false,
            primaryKey: false,
            startIncrementFrom: 0,
            referenceEntityName: '',
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: '',
          },
          {
            name: 'systemType',
            type: 'STRING',
            isUnique: false,
            primaryKey: false,
            startIncrementFrom: 0,
            referenceEntityName: '',
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: '',
          },
          {
            name: 'systemString',
            type: 'STRING',
            isUnique: false,
            primaryKey: false,
            startIncrementFrom: 0,
            referenceEntityName: '',
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: '',
          },
          {
            name: 'systemNumber',
            type: 'NUMBER',
            isUnique: false,
            primaryKey: false,
            startIncrementFrom: 0,
            referenceEntityName: '',
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: 0,
          },
          {
            name: 'systemBoolean',
            type: 'BOOLEAN',
            isUnique: false,
            primaryKey: false,
            startIncrementFrom: 0,
            referenceEntityName: '',
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: false,
          },
          {
            name: 'systemTimestamp',
            type: 'TIMESTAMP',
            isUnique: false,
            primaryKey: false,
            startIncrementFrom: 0,
            referenceEntityName: '',
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: new Date().toISOString(),
          },
        );
      }
    }

    this.entityData.data.push(JSON.parse(JSON.stringify(this.newVariableData)));

    this.newVariableData = {
      name: '',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      multiple: false,
      extendedAttributes: false,
      id: '',
      data: [],
      defaultValue: '',
    };

    this.addVariableFlag = false;
  }

  uniqueVariableNameCheck() {
    if (this.newVariableData.name == '') {
      return false;
    }
    for (let i = 0; i < this.entityData.data.length; i++) {
      if (this.entityData.data[i].name == this.newVariableData.name) {
        return false;
      }
    }
    return true;
  }
  // check table name
  uniqueTableNameCheck() {
    if (this.entityData.name == '') {
      return false;
    }
    for (let i = 0; i < this.ootbEntityList.length; i++) {
      if (this.entityData.id == this.ootbEntityList[i].id) {
        return false;
      }
    }
    return true;
  }

  variableNameChanged() {
    this.newVariableData.name = this.newVariableData.name.replace(
      /[^a-zA-Z_]/g,
      ''
    );
  }
  tableNameChanged() {
    this.entityData.name = this.entityData.name.replace(/[^a-zA-Z0-9]/g, '');
    if (!this.index) {
      this.modelName = this.entityData.name;
    } else {
      this.modelName = this.modelNameInput + '_' + this.entityData.name;
    }

    // this.changeForigenKeyName();
  }
  //   changeForigenKeyName() {
  //     for(let i=0; i < this.entityData.data.length; i ++) {
  // if(this.entityData.data[i].name == 'parent_id') {
  // this.entityData.data[i].referenceEntityName = this.modelNameInput;
  // }
  //     }
  //   }
  SaveTableChanges() {
    if (!('id' in this.entityData)) return false;

    let ok = true;
    if (this.entityData.id == '') {
      this.entityData.name = this.entityData.name.replace(/[^a-zA-Z0-9]/g, '');
      this.entityData.id = this.entityData.name;
      ok = this.uniqueTableNameCheck();
    }

    if (!ok) {
      alert('same Data Model name already exists, please change');
      return false;
    }

    for (let j = 0; j < this.entityData.data.length; j++) {
      if (this.entityData.data[j].type == 'ARRAY_OF_OBJECT') {
        this.entityData.data[j].data[2].referenceEntityName = this.entityData.id;
      }
    }

    this.dowWithLoadingScreen(async () => {
      await this.ootbEntityService.create(this.entityData as Entity);
    });

    this.exit.emit(true);
    return true;
  }

  generateTableId(): string {
    const randomInt = Math.floor(Math.random() * 10000000000000);
    const timestamp = Date.now();
    const combinedValue = randomInt + timestamp; // Combine random number and timestamp
    const base64String = btoa(String(combinedValue));
    return base64String.substring(0, 6); // Extract first 6 characters
  }
 dowWithLoadingScreen = async (expression :Function) => {
    try {
      this.loading.isLoading = true
      await expression();
    } finally {
      this.loading.isLoading = false
    }
  }

  deleteVariable(index: number) {
    this.entityData.data.splice(index, 1);
  }
  // Add this property at the top of the class with other properties
  editingVariableIndex: number | null = null;

  editVariable(index: number) {
    // Store the index of variable being edited
    this.editingVariableIndex = index;
    
    // Copy the variable data to the form
    this.newVariableData = JSON.parse(JSON.stringify(this.entityData.data[index]));
    
    // Set appropriate flags based on variable type
    if (this.newVariableData.type === 'STRING' || this.newVariableData.type === 'NUMBER'|| this.newVariableData.type === 'BOOLEAN') {
      this.newVariableUniqueFlag = true;
    } else {
      this.newVariableUniqueFlag = false;
    }
    
    // Show the edit dialog
    this.addVariableFlag = true;
    this.addVariableButtonFlag = true;
  }

  updateVariable() {
    if (this.editingVariableIndex === null) return;
    
    const currentVar = this.entityData.data[this.editingVariableIndex];
    
    // If editFlag is true, allow editing all properties
    if (this.editFlag) {
      this.newVariableData.name = this.newVariableData.name.replace(/[^a-zA-Z_]/g, '');
      
      // Check if name is unique (unless it's the same as the original name)
      if (this.newVariableData.name !== currentVar.name) {
        const nameExists = this.entityData.data.some(
          (item, idx) => item.name === this.newVariableData.name && idx !== this.editingVariableIndex
        );
        if (nameExists) {
          alert('A variable with this name already exists');
          return;
        }
      }
      
      // Update all properties
      this.entityData.data[this.editingVariableIndex] = JSON.parse(JSON.stringify(this.newVariableData));
    } else {
      // If editFlag is false, only allow updating default value
      currentVar.defaultValue = this.newVariableData.defaultValue;
    }
    
    // Reset and close modal
    this.editingVariableIndex = null;
    this.newVariableData = {
      name: '',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      id: '',
      data: [],
      defaultValue: '',
    };
    this.addVariableFlag = false;
  }

  checkDeleteFalgForObject(index: number) {
    var flag = true;
    if (this.entityData.data[index].type == 'ARRAY_OF_OBJECT') {
      for (let i = 0; i < this.entityData.data[index].data.length; i++) {
        if (this.entityData.data[index].data[i].type == 'ARRAY_OF_OBJECT') {
          return false;
        }
      }
    }
    return flag;
  }

  exitToEntityList() {
    this.exit.emit(true);
  }

  confirmExit() {
    this.showExitWarning = false;
    this.exitToEntityList();
  }

  cancelExit(event?: MouseEvent) {
    if (event) {
      // Prevent the event from bubbling up to parent components
      event.stopPropagation();
    }
    this.showExitWarning = false;
  }

  hasValidId(): boolean {
    if (!('id' in this.entityData)) return true;
    if ('id' in this.entityData && this.entityData.id == '') return false;
    else return true;
  }

  ngOnDestroy() {
    if (this.index === 0) {
      document.removeEventListener('keydown', this.handleEscapeKey.bind(this));
    }
  }
}
